/**
 * Voice Models
 *
 * Shared model and voice defaults for Losi speech features.
 *
 * Primary responsibilities:
 * - Keep text-to-speech and transcription defaults aligned
 * - Allow provider-specific environment overrides
 *
 * Dependencies:
 * - OpenAI-compatible audio APIs
 */

export const DEFAULT_COPY_AZURE_TTS_MODEL = 'gpt-4o-mini-tts-2025-12-15'
export const DEFAULT_COPY_AZURE_TRANSCRIPTION_MODEL = 'gpt-4o-transcribe'
export const DEFAULT_COPY_AZURE_PORTKEY_PROVIDER = '@copy-of-infracl-9de808'
export const DEFAULT_REALTIME_MODEL = 'gpt-realtime-2'
export const DEFAULT_FAST_REALTIME_MODEL = 'gpt-realtime-mini-2025-12-15'
export const DEFAULT_REALTIME_TRANSCRIPTION_MODEL = 'gpt-realtime-whisper'

// Amazon Nova Sonic 2 (bidirectional speech-to-speech) is available when AWS
// SigV4 credentials are wired up. When configured, it becomes the preferred
// realtime voice model and the OpenAI Realtime constants above are used as
// fallback values.
export const NOVA_SONIC_MODEL_ID = 'amazon.nova-2-sonic-v1:0'
export const NOVA_SONIC_VOICES = ['matthew', 'tiffany'] as const
export type NovaSonicVoice = (typeof NOVA_SONIC_VOICES)[number]
export const DEFAULT_NOVA_SONIC_VOICE: NovaSonicVoice = 'matthew'

// AWS Polly is the preferred TTS backend when SigV4 auth is available; the
// Copy Azure/OpenAI path stays as fallback for environments without AWS creds.
export const DEFAULT_POLLY_TTS_VOICE = 'Joanna'
export const DEFAULT_POLLY_TTS_ENGINE = 'neural'

export const POLLY_TTS_ENGINE_VALUES = ['standard', 'neural', 'long-form', 'generative'] as const

export type PollyTtsEngine = (typeof POLLY_TTS_ENGINE_VALUES)[number]

export const COPY_AZURE_TTS_MODEL_VALUES = [
  'gpt-4o-mini-tts-2025-12-15',
  'gpt-4o-mini-tts',
  'tts-1-hd',
  'tts-1'
] as const

export const COPY_AZURE_TTS_VOICE_VALUES = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'fable',
  'onyx',
  'nova',
  'sage',
  'shimmer',
  'verse'
] as const

export const COPY_AZURE_TRANSCRIPTION_MODEL_VALUES = [
  'gpt-4o-transcribe',
  'gpt-4o-mini-transcribe',
  'gpt-4o-transcribe-diarize',
  'whisper-1'
] as const

export const COPY_AZURE_REALTIME_MODEL_VALUES = [
  'gpt-realtime-2',
  'gpt-realtime',
  'gpt-realtime-mini-2025-12-15',
  'gpt-4o-realtime-preview'
] as const

export type CopyAzureTtsModel = (typeof COPY_AZURE_TTS_MODEL_VALUES)[number]
export type CopyAzureTtsVoice = (typeof COPY_AZURE_TTS_VOICE_VALUES)[number]
export type CopyAzureTranscriptionModel = (typeof COPY_AZURE_TRANSCRIPTION_MODEL_VALUES)[number]

const readEnv = (name: string) => {
  const value = process.env[name]?.trim()
  return value ? value : undefined
}

// --------------

export function getCopyAzureTtsModel() {
  return (
    readEnv('COPY_AZURE_TTS_MODEL') ||
    readEnv('PORTKEY_TTS_MODEL') ||
    readEnv('OPENAI_TTS_MODEL') ||
    DEFAULT_COPY_AZURE_TTS_MODEL
  )
}

// --------------

export function getPollyTtsVoice(): string {
  return readEnv('POLLY_TTS_VOICE') || DEFAULT_POLLY_TTS_VOICE
}

// --------------

export function getPollyTtsEngine(): PollyTtsEngine {
  const raw = readEnv('POLLY_TTS_ENGINE')?.toLowerCase()
  if (raw && POLLY_TTS_ENGINE_VALUES.some((value) => value === raw)) {
    return raw as PollyTtsEngine
  }
  return DEFAULT_POLLY_TTS_ENGINE
}

// --------------

export function getCopyAzureTranscriptionModel() {
  return (
    readEnv('COPY_AZURE_TRANSCRIPTION_MODEL') ||
    readEnv('PORTKEY_TRANSCRIPTION_MODEL') ||
    readEnv('OPENAI_TRANSCRIPTION_MODEL') ||
    DEFAULT_COPY_AZURE_TRANSCRIPTION_MODEL
  )
}

// --------------

// Env-only presence check for AWS SigV4 realtime auth. No network probe: this
// is intentionally synchronous so it can gate model selection cheaply.
export function hasAwsRealtimeAuth(): boolean {
  const staticKeys = !!(readEnv('AWS_ACCESS_KEY_ID') && readEnv('AWS_SECRET_ACCESS_KEY'))
  const profile = !!readEnv('AWS_PROFILE')
  const roleArn = !!readEnv('AWS_ROLE_ARN')
  const webIdentity = !!readEnv('AWS_WEB_IDENTITY_TOKEN_FILE')
  const containerCreds = !!readEnv('AWS_CONTAINER_CREDENTIALS_FULL_URI') ||
    !!readEnv('AWS_CONTAINER_CREDENTIALS_RELATIVE_URI')

  return staticKeys || profile || roleArn || webIdentity || containerCreds
}

// --------------

export function getRealtimeModel() {
  const explicit = readEnv('OPENAI_REALTIME_MODEL')
  if (explicit) return explicit

  // Prefer Nova Sonic when AWS auth is available; otherwise fall back to the
  // OpenAI Realtime default so voice paths keep working without AWS creds.
  if (hasAwsRealtimeAuth()) return NOVA_SONIC_MODEL_ID
  return DEFAULT_REALTIME_MODEL
}

// --------------

export function getRealtimeTranscriptionModel() {
  return readEnv('OPENAI_REALTIME_TRANSCRIPTION_MODEL') || DEFAULT_REALTIME_TRANSCRIPTION_MODEL
}

// --------------

export function getCopyAzureRealtimeProvider() {
  return (
    readEnv('COPY_AZURE_PORTKEY_PROVIDER') ||
    readEnv('PORTKEY_AZURE_PROVIDER') ||
    DEFAULT_COPY_AZURE_PORTKEY_PROVIDER
  )
}

// --------------

export function getCopyAzurePortkeyProvider() {
  return getCopyAzureRealtimeProvider()
}

// --------------

export function normalizePortkeyProvider(provider: string) {
  const value = provider.trim()
  if (!value) return ''

  return value.startsWith('@') ? value : `@${value}`
}

// --------------

export function stripPortkeyProvider(model: string) {
  const value = model.trim()
  if (!value.startsWith('@')) return value

  const separatorIndex = value.indexOf('/')
  return separatorIndex >= 0 ? value.slice(separatorIndex + 1) : value
}

// --------------

export function getPortkeyProviderModel(model: string, provider = getCopyAzurePortkeyProvider()) {
  const value = model.trim()
  if (!value) return value
  if (value.startsWith('@') && value.includes('/')) return value

  const normalizedProvider = normalizePortkeyProvider(provider)
  return normalizedProvider ? `${normalizedProvider}/${value}` : value
}

// --------------

export function isCopyAzureTtsVoice(value: unknown): value is CopyAzureTtsVoice {
  return typeof value === 'string' && COPY_AZURE_TTS_VOICE_VALUES.some((voice) => voice === value)
}

// --------------

export function supportsTtsInstructions(model: string) {
  return stripPortkeyProvider(model).startsWith('gpt-4o-mini-tts')
}

// --------------

export function supportsTtsSpeed(model: string) {
  const baseModel = stripPortkeyProvider(model)
  return baseModel === 'tts-1' || baseModel === 'tts-1-hd'
}
